import { isEqual } from 'lodash'
import { action, computed, makeAutoObservable, observable } from 'mobx'

import { IRootStore } from 'store/types'
import { SignboardFormat, SignboardSize } from 'sections/LKSection/modules/OrderModule/types'
import { EMPLOYEE_ROLES, OWNER_ROLES } from 'sections/LKSection/shared/PickPointSharedModule/data'
import { PickPointFilesViewModel } from 'sections/LKSection/shared/PickPointSharedModule/models/PickPointFilesViewModel'
import { TimetableModel } from 'sections/LKSection/shared/PickPointSharedModule/models/TimetableModel/TimetableModel'
import { USER_PERMISSION } from 'shared/types/types'

import { DEFAULT_PVZ_FEATURES } from '../../data'
import { PvzFeature, PvzInitialStateType, TimetableStatus } from '../../types/types'
import { SignboardDTO, UpdateSignboardData } from '../../types/typesDTO'
import { Pvz } from '../entity/Pvz'
import { PvzAreaViewModel } from '../models/PvzAreaModel'
import { ViewPvzApiStore } from './ViewPvzApiStore'

export class ViewPvzUiStore {
  @observable page = 1
  @observable pageSize = 20

  @observable selectedPvz: Pvz | null = null
  @observable switchedPvzId = 0

  @observable racksCount = ''
  @observable cellsCount = ''
  @observable maxCellsCount = ''

  @observable description = ''
  @observable courierWay = ''
  @observable alarmSystem = ''
  @observable dressingRoomsCount = ''
  @observable timetableEditStatus = TimetableStatus.none
  @observable signboardData: SignboardDTO = {
    isCheck: false,
    signboardFormat: SignboardFormat.none,
    signboardSize: SignboardSize.none,
    signboardUrls: [],
  }
  @observable features: PvzFeature[] = DEFAULT_PVZ_FEATURES

  @observable readonly areaModel = new PvzAreaViewModel(this)
  @observable readonly fileList = new PickPointFilesViewModel()
  @observable readonly timetable = new TimetableModel()

  @observable isSwitchPvzLoading = false
  @observable initialized = false

  @observable private readonly apiStore = new ViewPvzApiStore(this)

  private initialState: PvzInitialStateType | null = null

  constructor(readonly rootStore: IRootStore) {
    makeAutoObservable(this)
  }

  get appStore() {
    return this.rootStore.appStore
  }

  get pvzStore() {
    return this.rootStore.pvzStore
  }

  get pvzList() {
    return this.pvzStore.list
  }

  get loadingPvzList() {
    return this.pvzStore.loading
  }

  get isNoChanges() {
    return isEqual(this.initialState, this.hashFormState)
  }

  get hashFormState(): PvzInitialStateType {
    const fileList = this.fileList

    return {
      description: this.description.trim(),
      courierWay: this.courierWay.trim(),
      alarmSystem: this.alarmSystem.trim(),
      dressingRoomsCount: this.dressingRoomsCount,
      videos: fileList.videos.map((video) => video.url),
      documents: fileList.documents.map((document) => document.url),
      photos: fileList.photos.map((photo) => ({ url: photo.url, isPublic: photo.isPublic })),
      racksCount: this.racksCount,
      cellsCount: this.cellsCount,
      maxCellsCount: this.maxCellsCount,
      features: this.features.map((feature) => feature.checked),
    }
  }

  @computed
  isOwner = (pvzId: number) => {
    const pvzRoleInfo = this.appStore.user.info.roles?.find((item) => item.pickpoint_id === pvzId)

    return Boolean(
      pvzRoleInfo?.is_owner &&
        (pvzRoleInfo.permission === USER_PERMISSION.PERM_OWNER ||
          pvzRoleInfo.permission === USER_PERMISSION.PERM_FIRM_OWNER),
    )
  }

  hasAtLeastOneRolesOnPvz = (pvzId: number, roles: USER_PERMISSION[]) => {
    const roleItem = this.appStore.user.info.roles?.find((role) => role.pickpoint_id === pvzId)

    if (!roleItem) {
      return false
    }

    return roles.some((role) => role === roleItem.permission)
  }

  isManager(pvzId: number) {
    return this.hasAtLeastOneRolesOnPvz(pvzId, OWNER_ROLES)
  }

  isEmployee(pvzId: number) {
    return this.hasAtLeastOneRolesOnPvz(pvzId, EMPLOYEE_ROLES)
  }

  getPvz = (pvzId: number) => {
    return this.pvzStore.getEntity(pvzId) ?? null
  }

  init = (pvz: Pvz) => {
    const featuresSet = new Set(pvz.features)

    const features = this.features.map((feature) => {
      return { ...feature, checked: featuresSet.has(feature.value) }
    })

    this.setDescription(pvz.description)
    this.setCourierWay(pvz.courierWay)
    this.setAlarmSystem(pvz.alarmSystem)
    this.setDressingRoomsCount(pvz.dressingRoomsCount.toString())
    this.setRacksCount(pvz.racksCount.toString())
    this.setCellsCount(pvz.cellsCount.toString())
    this.setMaxCellsCount(pvz.maxCellsCount.toString())
    this.setFeatures(features)

    this.initFiles(pvz)
    this.areaModel.init(pvz)
    this.timetable.init(pvz)

    this.setInitialState(this.hashFormState)
    this.setInitialized(true)
  }

  initFiles = (pvz: Pvz) => {
    if (pvz.videos?.length && pvz.videos[0]?.url) {
      this.fileList.setVideoURL(pvz.videos[0].url)
      this.fileList.setVideoFile({ url: '', name: '', size: 0 })
    }

    this.fileList.setPhotos([...pvz.photos, ...pvz.documents])
  }

  @action
  setInitialState = (value: PvzInitialStateType) => {
    this.initialState = value
  }

  @action
  setPage = (value: number) => {
    this.page = value
  }

  @action
  setPageSize = (value: number) => {
    this.pageSize = value
  }

  @action
  setSelectedPvz = (value: Pvz | null) => {
    if (this.selectedPvz?.id !== value?.id) {
      this.reset()
    }

    this.selectedPvz = value

    if (value) {
      this.init(value)
    } else {
      this.setInitialized(false)
    }
  }

  @action
  setSwitchedPvzId = (value: number) => {
    this.switchedPvzId = value
  }

  @action
  setRacksCount = (value: string) => {
    this.racksCount = value
  }

  @action
  setCellsCount = (value: string) => {
    this.cellsCount = value
  }

  @action
  setMaxCellsCount = (value: string) => {
    this.maxCellsCount = value
  }

  @action
  setDescription = (value: string) => {
    this.description = value
  }

  @action
  setCourierWay = (value: string) => {
    this.courierWay = value
  }

  @action
  setAlarmSystem = (value: string) => {
    this.alarmSystem = value
  }

  @action
  setDressingRoomsCount = (value: string) => {
    this.dressingRoomsCount = value
  }

  @action
  setTimetableEditStatus = (value: TimetableStatus) => {
    this.timetableEditStatus = value
  }

  @action
  setIsSwitchPvzLoading = (value: boolean) => {
    this.isSwitchPvzLoading = value
  }

  @action
  setInitialized = (value: boolean) => {
    this.initialized = value
  }

  @action
  setSignboardData = (value: SignboardDTO) => {
    this.signboardData = value
  }

  @action
  setFeatures = (value: PvzFeature[]) => {
    this.features = value
  }

  reset = () => {
    this.setDescription('')
    this.setDressingRoomsCount('')
    this.fileList.reset()
    this.setInitialized(false)
    this.setFeatures(DEFAULT_PVZ_FEATURES)
  }

  async getPvzList() {
    return await this.pvzStore.loadList({ page: this.page, pageSize: this.pageSize })
  }

  async updatePvz(pvzId: number, updatedFields?: string[]) {
    return await this.apiStore.updatePvz(pvzId, updatedFields)
  }

  async switchPvz(pvzId: number) {
    return await this.apiStore.switchPvz(pvzId)
  }

  async getTimetableEditStatus(pvzId: number) {
    return await this.apiStore.getTimetableEditStatus(pvzId)
  }

  async getPvzSignboardList(pvzId: number) {
    return await this.apiStore.getPvzSignboardList(pvzId)
  }

  async updatePvzSignboard(patch: UpdateSignboardData) {
    return await this.apiStore.updatePvzSignboard(patch)
  }
}
